// Winter storage contracts: the service catalog, the default rates and terms, and the price of one
// boat's contract. Pure (no Firebase, no DOM), shared by the office's contract builder and printed
// agreement (storageContracts.js), the customer portal (portal.js) and the server, which re-prices
// a customer's changes and the signed copy (functions/portalContracts.js). Copied to
// functions/shared/ before deploy. Money goes through money.js, so every screen agrees to the cent.
//
// Prices come from Settings › Storage rates (/pricing/storageContract) merged over STORAGE_DEFAULTS
// (storageSettings). Storage is length × beam × the boat's length tier, never under the minimum;
// sales tax is on storage only; the environmental fee is on storage + services. T&M services have
// no fixed price and stay out of the total.

import { toCents, toDollars, priceTotals } from './money.js';

// `hours` is the default Estimated_Hours for an op created from a row whose opCode
// isn't in /opcodes (addJob → defaultOpHours in app.js); /opcodes' own Hours wins. `opType`
// is its Op_Type the same way (defaultOpType).
export const STORAGE_SERVICE_CATALOG = [
  { service: 'Shrink wrap & frame — up to 30 ft', category: 'Storage & cover', rate: '$21.50/ft', amount: 21.50, unit: 'perFoot', season: 'shrinkwrap', opCode: '', minLength: 0, maxLength: 30 },
  { service: 'Shrink wrap & frame — 31 to 40 ft', category: 'Storage & cover', rate: '$25.00/ft', amount: 25.00, unit: 'perFoot', season: 'shrinkwrap', opCode: '', minLength: 31, maxLength: 40 },
  { service: 'Shrink wrap & frame — 41 to 50 ft', category: 'Storage & cover', rate: '$28.00/ft', amount: 28.00, unit: 'perFoot', season: 'shrinkwrap', opCode: '', minLength: 41, maxLength: 50 },
  { service: 'Shrink wrap & frame — over 50 ft', category: 'Storage & cover', rate: 'T&M', amount: null, unit: 'tm', season: 'shrinkwrap', opCode: '', minLength: 51, maxLength: null },
  { service: 'Shrink wrap door', category: 'Storage & cover', rate: '$68/door', amount: 68, unit: 'flat', season: 'shrinkwrap', opCode: '' },
  { service: 'Remove & dispose of shrinkwrap in spring', category: 'Storage & cover', rate: '$6.00/ft', amount: 6.00, unit: 'perFoot', season: 'spring', opCode: '' },
  { service: 'Install owner-provided winter cover', category: 'Storage & cover', rate: '$180/hr', amount: null, unit: 'tm', season: 'winter', opCode: '' },
  { service: 'Remove canvas — store in boat', category: 'Storage & cover', rate: '$95', amount: 95, unit: 'flat', season: 'winter', opCode: '' },
  { service: 'Canvas — send out for cleaning', category: 'Storage & cover', rate: '$95', amount: 95, unit: 'flat', season: 'winter', opCode: '' },
  { service: 'Reinstall canvas', category: 'Storage & cover', rate: '$95', amount: 95, unit: 'flat', season: 'spring', opCode: '' },
  { service: 'Haul customer-owned mooring — incl. pressure wash & storage', category: 'Storage & cover', rate: '$0.80/lb', amount: null, unit: 'tm', season: 'winter', opCode: '' },
  { service: 'Winterize inboard — 4 cyl or less', category: 'Engine', rate: '$275', amount: 275, unit: 'flat', season: 'winter', opCode: 'WIN-4CYL', hours: 2, opType: 'Mechanical' },
  { service: 'Commission inboard — 4 cyl or less', category: 'Engine', rate: '$275', amount: 275, unit: 'flat', season: 'spring', opCode: 'COM-4CYL', hours: 2, opType: 'Mechanical' },
  { service: 'Winterize outboard — over 50hp', category: 'Engine', rate: '$215', amount: 215, unit: 'flat', season: 'winter', opCode: '' },
  { service: 'Commission outboard — over 50hp', category: 'Engine', rate: '$215', amount: 215, unit: 'flat', season: 'spring', opCode: '' },
  { service: 'Small outboard up to 50hp — winterize', category: 'Engine', rate: '$170', amount: 170, unit: 'flat', season: 'winter', opCode: '' },
  { service: 'Small outboard — remove, store inside', category: 'Engine', rate: '$325', amount: 325, unit: 'flat', season: 'winter', opCode: '' },
  { service: 'Small outboard — reinstall in spring', category: 'Engine', rate: '$175', amount: 175, unit: 'flat', season: 'spring', opCode: '' },
  { service: 'Winterize generator', category: 'Engine', rate: '$195', amount: 195, unit: 'flat', season: 'winter', opCode: '' },
  { service: 'Commission generator', category: 'Engine', rate: '$195', amount: 195, unit: 'flat', season: 'spring', opCode: '' },
  { service: 'Battery removal', category: 'Batteries', rate: '$175', amount: 175, unit: 'flat', season: 'winter', opCode: 'BAT-RMV', hours: 0.5, opType: 'Batteries' },
  { service: 'Battery storage, charging & testing', category: 'Batteries', rate: '$60/ea', amount: 60, unit: 'flat', season: 'winter', opCode: '' },
  { service: 'Install batteries & test for draw', category: 'Batteries', rate: '$175', amount: 175, unit: 'flat', season: 'spring', opCode: 'BAT-INS', hours: 0.5, opType: 'Batteries' },
  { service: 'Winterize all necessary water systems', category: 'Water systems', rate: 'T&M', amount: null, unit: 'tm', season: 'winter', opCode: '' },
  { service: 'Commission all necessary water systems', category: 'Water systems', rate: 'T&M', amount: null, unit: 'tm', season: 'spring', opCode: '' },
  { service: 'Unstep mast', category: 'Rigging', rate: '$15.75/ft', amount: 15.75, unit: 'perFoot', season: 'winter', opCode: 'RIG-UNS', opType: 'Rigging' },
  { service: 'Step mast', category: 'Rigging', rate: '$15.75/ft', amount: 15.75, unit: 'perFoot', season: 'spring', opCode: 'RIG-STP', opType: 'Rigging' },
  { service: 'Mast storage', category: 'Rigging', rate: '$6.50/ft', amount: 6.50, unit: 'perFoot', season: 'winter', opCode: '' },
  { service: 'Outdrive service — removal, storage & reinstall', category: 'Outdrive', rate: 'T&M', amount: null, unit: 'tm', season: 'winter', opCode: '' },
  { service: 'Spring detailing — hull & deck wax', category: 'Spring detailing', rate: '$22.75/ft', amount: 22.75, unit: 'perFoot', season: 'spring', opCode: '' },
];

// Shared with settings.js's storage-rates editor AND app.js's contract
// builder/printed agreement — a single source so a boat's printed contract
// can never show different tiers/terms than what Settings displays, and so
// the printed page always has real content (not blank fields) even before
// anyone has ever saved Settings > Storage rates.
export const STORAGE_DEFAULTS = {
  season: '',
  tiers: [
    { from: 0, to: 20, rate: 13.35 },
    { from: 20.1, to: 26, rate: 11.35 },
    { from: 26.1, to: 32, rate: 10.15 },
    { from: 32.1, to: 39, rate: 9.60 },
    { from: 39.1, to: 50, rate: 8.65 },
    { from: 50.1, to: 70, rate: 8.25 },
  ],
  minCharge: 2000,
  deposit: 400,
  envPct: 2.5,
  taxPct: 8.625,
  launchCredit: 100,
  dates: { returnBy: '', seasonStart: '', seasonEnd: '', allLaunchBy: '' },
  mappings: [],
  // Transcribed from the 2025-26 Winter Storage & Spring
  // Commissioning Agreement (the source PDF the whole handoff was modeled
  // on) — a real default, not placeholder text, though it's exactly the
  // kind of thing that should be reviewed/updated here each season.
  termsText: `The following terms and conditions apply between {{business}} ("the Marina") and you, the boat owner ("Owner"), for the winter storage and launching season. By signing the Agreement, Owner agrees to be bound by them.

- Haul and launch dates are assigned by the Marina based on Owner's request, provided a signed Agreement is received by the return-by date above. Boats in arrears are not scheduled until accounts are current.
- Payment in full for all services rendered, including subcontractor fees, is required before the vessel is launched or leaves the marina.
- All vessels must launch or be scheduled to launch by the season's all-boats-launch-by date; vessels relocated for parking or missed launch dates incur a relocation charge at the prevailing rate.
- Owner shall carry hull and liability insurance. The Marina is not responsible for any boat, its contents, or equipment in the event of loss or damage.
- Outside contractors require the Marina's advance permission and certificates of insurance; contractor work bills through the Marina with a 20% facilities fee.
- No electrical cords left plugged in during storage; no electric heaters or open flames on Marina premises.
- Owner is responsible for checking the boat and removing water, snow, or ice; the Marina is not responsible for winter covers, including shrink wrap, on outside storage.
- Drain plugs are Owner's responsibility. All batteries must be removed from the boat; holding tanks pumped if not selected on this contract.
- No discharge of any substance, including non-toxic antifreeze, on Marina grounds. All antifreeze must be captured, contained, and removed.
- No open-air sanding or grinding; bottom scraping/sanding only over tarps with debris collected and disposed of off site. All used oil, batteries, chemicals, and paints must be removed from the marina.
- Trailers must be removed within 48 hours of launch; non-slip-renting boats have two days of dock space after launch, then transient dockage at the prevailing rate per LOA foot per night.
- Dock lines, fenders, batteries, and bilge pumps must be aboard and operative prior to launch; the marina must have key/combo access for bilge inspection at launch.
- The Marina may terminate this Agreement for any reason; liability is limited to a refund of the unearned, prorated amount paid for storage or services not delivered.
- Unpaid invoices past ten (10) days may be charged to the card on file; refused charges accrue a 1.5% monthly service charge, and Owner grants the Marina a lien on the boat for outstanding amounts.
- Sailboats stored mast-in are at Owner's risk; sails must be removed before hauling or the Marina will remove them at the current hourly rate.
- Pets leashed at all times on marina property. Stored trailers must be identified with the owner's name and carry a signed trailer storage contract.`,
};

// Storage season runs fall-to-spring, so "this season" is keyed off the year
// it started in (e.g. Sept 2026 through the following spring is "2026-27").
export function currentSeasonLabel() {
  const now = new Date();
  const y = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return `${y}-${String((y + 1) % 100).padStart(2, '0')}`;
}

/** Settings › Storage rates as saved, over the defaults (the same merge the Settings editor does). */
export function storageSettings(saved) {
  const s = saved && typeof saved === 'object' ? saved : {};
  return { ...STORAGE_DEFAULTS, ...s, dates: { ...STORAGE_DEFAULTS.dates, ...(s.dates || {}) } };
}

/** The rate tier for a boat length, from the saved tiers (else the default ones). */
export function storageTierFor(settings, length) {
  const tiers = settings?.tiers?.length ? settings.tiers : STORAGE_DEFAULTS.tiers;
  return tiers.find(t => length >= t.from && length <= t.to) || null;
}

/** Whether a catalog row fits this boat (the shrink-wrap rows are length tiers; the rest fit any boat). */
export function contractServiceApplies(entry, boat) {
  if (entry.minLength == null && entry.maxLength == null) return true;
  const length = Number(boat?.Boat_Length) || 0;
  if (entry.minLength != null && length < entry.minLength) return false;
  if (entry.maxLength != null && length > entry.maxLength) return false;
  return true;
}

/** A service's price for this boat, in dollars (per-foot × length, flat, or 0 for T&M). */
export function contractServicePrice(entry, boat) {
  const length = Number(boat?.Boat_Length) || 0;
  if (entry.unit === 'perFoot') return entry.amount * length;
  if (entry.unit === 'flat') return entry.amount;
  return 0;
}

/** Only real catalog services that fit the boat, as { name: true }. */
export function cleanSelections(raw, boat) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  STORAGE_SERVICE_CATALOG.forEach(e => { if (raw[e.service] === true && contractServiceApplies(e, boat)) out[e.service] = true; });
  return out;
}

/**
 * The price of one boat's contract. settings: storageSettings(...). Dollar amounts, each rounded to
 * the cent the way money.js does. `totals` is what Boat.Storage_Contract.totals stores.
 */
export function priceStorageContract(boat, selections, settings) {
  const s = settings || storageSettings(null);
  const length = Number(boat?.Boat_Length) || 0;
  const beam = Number(boat?.Boat_Beam) || 0;
  const minCharge = s.minCharge ?? 2000;
  const tier = storageTierFor(s, length);
  const unitFt = length * beam;
  const storageBase = tier ? unitFt * tier.rate : 0;
  const belowMin = storageBase > 0 && storageBase < minCharge;
  const storage = belowMin ? minCharge : storageBase;

  const sel = selections || {};
  const lines = STORAGE_SERVICE_CATALOG.filter(e => contractServiceApplies(e, boat)).map(e => ({
    service: e.service, category: e.category, rate: e.rate, tm: e.unit === 'tm', selected: !!sel[e.service],
    price: e.unit === 'tm' ? null : contractServicePrice(e, boat),
  }));
  const services = lines.reduce((a, l) => a + (l.selected && !l.tm ? l.price : 0), 0);

  const taxPct = Number(s.taxPct ?? STORAGE_DEFAULTS.taxPct) / 100;
  const envPct = Number(s.envPct ?? STORAGE_DEFAULTS.envPct) / 100;
  const c = priceTotals({
    subtotal: toCents(storage) + toCents(services),
    taxBase: toCents(storage), feeBase: toCents(storage) + toCents(services),
    taxRate: taxPct, feeRate: envPct,
  });
  const tax = toDollars(c.tax);
  const envFee = toDollars(c.fee);
  const total = toDollars(c.total);
  const deposit = s.deposit ?? 400;
  const balance = Math.max(total - deposit, 0);
  const half = balance / 2;
  return {
    length, beam, unitFt, tier, storageBase, belowMin, minCharge, taxPct, envPct, deposit,
    launchCredit: s.launchCredit ?? 100, lines,
    totals: { storage, tax, services, envFee, total },
    payments: { atSigning: deposit, beforeHaul: half, final: balance - half },
  };
}
